import {
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  Form,
  FormSubmitButton,
  PlacesAutocompleteFormField,
  SelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateFirmVirtualBusinessCard,
  useDeleteFirmVirtualBusinessCard,
} from "@/lib/data/react-query/firm/firm-business-card";
import { useFirmPhones } from "@/lib/data/react-query/firm/firm-phones";
import { FirmVirtualBusinessCardSchema } from "@/lib/data/schemas/firm/FirmSchema";
import {
  CitySchema,
  CountrySchema,
  EmailSchema,
  StateSchema,
  StreetAddressSchema,
  ZipCodeSchema,
} from "@/lib/data/schemas/CommonSchemaFields";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type FirmVirtualBusinessCard } from "@/lib/types/Firm";
import {
  formatAddress,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Globe, Mail, MapPin, Phone, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const FORM_ID = "add-firm-business-card-form";

const formSchema = FirmVirtualBusinessCardSchema.pick({
  fullName: true,
  email: true,
  website: true,
  street1: true,
  street2: true,
  city: true,
  state: true,
  zipCode: true,
  country: true,
}).extend({
  phoneId: z.string().min(1, "Phone number is required"),
  email: EmailSchema.optional().or(z.literal("")),
  website: z.string().optional().or(z.literal("")),
  street1: StreetAddressSchema.optional().or(z.literal("")),
  street2: StreetAddressSchema.optional().or(z.literal("")),
  city: CitySchema.optional().or(z.literal("")),
  state: StateSchema.optional().or(z.literal("")),
  zipCode: ZipCodeSchema.optional().or(z.literal("")),
  country: CountrySchema.optional().or(z.literal("")),
});

interface AddOrEditFirmBusinessCardModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  businessCard?: FirmVirtualBusinessCard;
}

export function AddOrEditFirmBusinessCardModal({
  open,
  setOpen,
  businessCard,
}: AddOrEditFirmBusinessCardModalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const { data: firmPhones } = useFirmPhones();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      fullName: businessCard?.fullName ?? "",
      phoneId: "",
      email: businessCard?.email ?? "",
      website: businessCard?.website ?? "",
      street1: businessCard?.street1 ?? "",
      street2: businessCard?.street2 ?? "",
      city: businessCard?.city ?? "",
      state: businessCard?.state ?? "",
      zipCode: businessCard?.zipCode ?? "",
      country: businessCard?.country ?? "",
    },
  });

  const createFirmBusinessCard = useCreateFirmVirtualBusinessCard();
  const deleteFirmBusinessCard = useDeleteFirmVirtualBusinessCard();
  const isFormLoading =
    createFirmBusinessCard.isPending || deleteFirmBusinessCard.isPending;

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (businessCard) {
      // TODO: Add update functionality when available
      setOpen(false);
    } else {
      createFirmBusinessCard.mutate(data, {
        onSuccess: () => {
          setOpen(false);
          form.reset();
        },
      });
    }
  };

  const handleDelete = () => {
    if (businessCard?.id) {
      deleteFirmBusinessCard.mutate({
        id: businessCard.id,
      });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const formattedAddress = businessCard
    ? formatAddress({
        street1: businessCard.street1,
        street2: businessCard.street2,
        city: businessCard.city,
        state: businessCard.state,
        zipCode: businessCard.zipCode,
        country: businessCard.country,
      })
    : null;

  const formattedPhoneNumber = businessCard?.phoneNumber
    ? formatLocalOrIntlPhoneNumber({ number: businessCard.phoneNumber })
    : null;

  if (businessCard) {
    return (
      <>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title="Business card details"
          description="View the business card information below"
          loading={isFormLoading}
          size="2xl"
          footer={
            hasActionPermission(
              ACTION_PERMISSION_KEYS.firmVirtualBusinessCard,
              ACTION_PERMISSION_VALUES.delete,
            ) ? (
              <div className="flex w-full justify-between">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setIsDeleting(true);
                  }}
                >
                  Delete business card
                </Button>
                <div />
              </div>
            ) : null
          }
        >
          <div className="overflow-y-auto">
            <div className="p-6">
              <Card className="w-full border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <User className="text-muted-foreground h-4 w-4" />
                        <Typography
                          variant="subHeading"
                          size="sm"
                          className="text-muted-foreground font-medium"
                        >
                          Full name
                        </Typography>
                      </div>
                      <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                        <Typography
                          size="md"
                          className="text-foreground font-medium"
                        >
                          {businessCard.fullName || "-"}
                        </Typography>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Phone className="text-muted-foreground h-4 w-4" />
                        <Typography
                          variant="subHeading"
                          size="sm"
                          className="text-muted-foreground font-medium"
                        >
                          Phone number
                        </Typography>
                      </div>
                      <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                        <Typography
                          size="md"
                          className="text-foreground font-medium"
                        >
                          {formattedPhoneNumber ||
                            businessCard.phoneNumber ||
                            "-"}
                        </Typography>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="text-muted-foreground h-4 w-4" />
                        <Typography
                          variant="subHeading"
                          size="sm"
                          className="text-muted-foreground font-medium"
                        >
                          Email
                        </Typography>
                      </div>
                      <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                        <Typography
                          size="md"
                          className="text-foreground font-medium"
                        >
                          {businessCard.email || "-"}
                        </Typography>
                      </div>
                    </div>

                    {businessCard.website && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Globe className="text-muted-foreground h-4 w-4" />
                          <Typography
                            variant="subHeading"
                            size="sm"
                            className="text-muted-foreground font-medium"
                          >
                            Website
                          </Typography>
                        </div>
                        <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                          <Typography
                            size="md"
                            className="text-foreground font-medium"
                          >
                            {businessCard.website}
                          </Typography>
                        </div>
                      </div>
                    )}

                    {formattedAddress && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="text-muted-foreground h-4 w-4" />
                          <Typography
                            variant="subHeading"
                            size="sm"
                            className="text-muted-foreground font-medium"
                          >
                            Address
                          </Typography>
                        </div>
                        <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                          <Typography
                            size="md"
                            className="text-foreground font-medium"
                          >
                            {formattedAddress}
                          </Typography>
                        </div>
                      </div>
                    )}

                    {!formattedAddress && (
                      <>
                        {businessCard.street1 && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="text-muted-foreground h-4 w-4" />
                              <Typography
                                variant="subHeading"
                                size="sm"
                                className="text-muted-foreground font-medium"
                              >
                                Street address
                              </Typography>
                            </div>
                            <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                              <Typography
                                size="md"
                                className="text-foreground font-medium"
                              >
                                {businessCard.street1}
                                {businessCard.street2 && (
                                  <span>
                                    <br />
                                    {businessCard.street2}
                                  </span>
                                )}
                              </Typography>
                            </div>
                          </div>
                        )}

                        {(businessCard.city ||
                          businessCard.state ||
                          businessCard.zipCode) && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="text-muted-foreground h-4 w-4" />
                              <Typography
                                variant="subHeading"
                                size="sm"
                                className="text-muted-foreground font-medium"
                              >
                                Location
                              </Typography>
                            </div>
                            <div className="bg-muted/40 border-border/50 rounded-lg border p-4">
                              <Typography
                                size="md"
                                className="text-foreground font-medium"
                              >
                                {[
                                  businessCard.city,
                                  businessCard.state,
                                  businessCard.zipCode,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                                {businessCard.country &&
                                  businessCard.country !== "United States" && (
                                    <span>, {businessCard.country}</span>
                                  )}
                              </Typography>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </AppModal>
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title="Delete business card"
          description="Are you sure you want to delete this business card? This action cannot be undone."
          buttonText="Delete"
          afterActionFn={handleDelete}
        />
      </>
    );
  }

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        isDirty={form.formState.isDirty}
        title="Add business card"
        description="Add a new business card to your firm"
        loading={isFormLoading}
        size="2xl"
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <FormCancelButton
              isDirty={form.formState.isDirty}
              setOpen={setOpen}
            />
            <FormSubmitButton
              submitFormId={FORM_ID}
              loading={createFirmBusinessCard.isPending}
              title="Create"
            />
          </div>
        }
      >
        <form
          id={FORM_ID}
          className="flex min-h-0 grow flex-col overflow-hidden"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="overflow-y-auto">
            <fieldset className={slideoverFieldsetClassName}>
              <TextFormField
                register={form.register("fullName")}
                label="Full name*"
                placeholder="e.g. John Doe"
              />
              <SelectFormField
                register={form.register("phoneId")}
                label="Phone number*"
                items={
                  firmPhones?.data.map((phone) => ({
                    value: phone.id,
                    label: phone.label,
                    context: phone.number,
                  })) ?? []
                }
                itemsKey="value"
                formatItemLabel={(item) => item?.label}
                formatItemContext={(item) => item?.context}
                placeholder="Select a phone number"
                isItemsLoading={!firmPhones}
              />
              <TextFormField
                register={form.register("email")}
                label="Email"
                placeholder="e.g. john.doe@example.com"
              />
              <TextFormField
                register={form.register("website")}
                label="Website"
                placeholder="e.g. https://example.com"
              />
              <PlacesAutocompleteFormField
                register={form.register("street1")}
                defaultValue={form.getValues("street1")}
                label="Street address 1"
                onAddressSelect={(address) => {
                  form.setValue("city", address.city, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  form.setValue("state", address.state, {
                    shouldValidate: true,
                  });
                  form.setValue("zipCode", address.zipCode, {
                    shouldValidate: true,
                  });
                  form.setValue("street1", address.streetAddress, {
                    shouldValidate: true,
                  });
                  form.setValue("country", address.country, {
                    shouldValidate: true,
                  });
                  form.setValue("street2", "");
                }}
              />
              <TextFormField
                register={form.register("street2")}
                label="Street address 2"
                placeholder="e.g. Apt 4B"
              />
              <TextFormField
                register={form.register("city")}
                label="City"
                placeholder="e.g. New York"
              />
              <TextFormField
                register={form.register("state")}
                label="State"
                placeholder="e.g. NY"
              />
              <TextFormField
                register={form.register("zipCode")}
                label="Zip code"
                placeholder="e.g. 10001"
              />
              <TextFormField
                register={form.register("country")}
                label="Country"
                placeholder="e.g. United States"
              />
            </fieldset>
          </div>
        </form>
      </AppModal>
    </Form>
  );
}
